import { AnimatePresence, motion } from "framer-motion";
import { useAtom } from "jotai";
import { FC, RefObject, createRef, useEffect, useRef, useState } from "react";
import { RemoveScroll } from "react-remove-scroll";

import { ChevronIcon } from "@theme/assets";
import {
  Box,
  Divider,
  Flex,
  MobileNavCategoryScreen,
  MsButton,
  NavLink,
  Text,
} from "@theme/components";
import { drawerOpenAtom } from "@theme/components/Navigation/Navigation";
import { NavCategory, NavigationDataProps } from "@theme/types";
import { useNavHeight } from "@theme/utils";

interface NavMobileDrawerProps {
  navCategories?: NavCategory[];
  additionalLinks: NavigationDataProps["additionalLinks"];
  blogLink: NavigationDataProps["blogLink"];
  loginLink: NavigationDataProps["loginLink"];
  accountAuthenticatedLink: NavigationDataProps["accountAuthenticatedLink"];
  isAuthenticated: boolean;
}

const NavLinkTextStyles = {
  color: "grey700",
  lineHeight: "1.75rem",
  letterSpacing: "-0.009rem",
};

export const NavMobileDrawer: FC<NavMobileDrawerProps> = ({
  navCategories = [],
  additionalLinks,
  blogLink,
  loginLink,
  accountAuthenticatedLink,
  isAuthenticated,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useAtom(drawerOpenAtom);
  const [selectedNavCategory, setSelectedNavCategory] = useState<NavCategory | null>(null);
  const mobileDrawerRef = useRef<HTMLDivElement>(null);
  const { navHeights } = useNavHeight();

  useEffect(() => {
    if (mobileDrawerRef.current) {
      mobileDrawerRef.current.scrollTop = 0;
    }
  }, [mobileDrawerRef]);

  useEffect(() => {
    if (!isDrawerOpen) setSelectedNavCategory(null);
  }, [isDrawerOpen]);

  const showAccordion = (index: number) => {
    setSelectedNavCategory(navCategories[index]);
  };

  const categoryRefs = useRef(
    Array(navCategories.length)
      .fill(null)
      .map(() => createRef<HTMLElement>()),
  );

  // Focus on the first navigation element when we open the mobile drawer
  useEffect(() => {
    if (isDrawerOpen) {
      categoryRefs.current[0].current?.focus();
    }
  }, [isDrawerOpen]);

  return (
    // only display on mobile screens
    <AnimatePresence>
      {isDrawerOpen && (
        <Box sx={{ display: ["block", null, null, "none"] }}>
          <RemoveScroll enabled={isDrawerOpen}>
            <motion.menu
              aria-label="Mobile Menu Dropdown"
              variants={{
                hide: { y: "-110%" },
                show: { y: 0 },
              }}
              initial="hide"
              animate={isDrawerOpen ? "show" : "hide"}
              transition={{ ease: [1, 0, 0, 1], duration: 0.3 }}
              ref={mobileDrawerRef}
              sx={{
                outline: "none",
                overflow: "hidden",
                width: "full",
                position: "absolute",
                top: "100%",
                left: 0,
                p: 0,
                m: 0,
                bg: "beige50",
                overflowY: "scroll",
                overflowX: "hidden",
                height: navHeights.map((height) => `calc(100vh - ${height}px)`),
                zIndex: -1,
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsDrawerOpen(false);
                }
              }}
            >
              <motion.div
                variants={{
                  hide: { x: "-100%" },
                  show: { x: 0 },
                }}
                initial="show"
                animate={selectedNavCategory === null ? "show" : "hide"}
                transition={{ ease: [1, 0, 0, 1], duration: 0.3 }}
                sx={{
                  position: "absolute",
                  display: "flex",
                  inset: 0,
                  flexDirection: "column",
                  alignSelf: "stretch",
                  gap: "s600",
                  px: ["s600", "s700"],
                  py: ["s400", "s700"],
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: "s400" }}>
                  {/* Shop All Category, we treat as a link */}
                  {navCategories.map((navCategory, index) =>
                    navCategory.isStaticLink ? (
                      <NavLink
                        ref={categoryRefs.current[index] as RefObject<HTMLAnchorElement>}
                        data-testid={`mobile-nav-${navCategory.slug}`}
                        key={index}
                        variant="box"
                        to={`${navCategory.pageSlug}`}
                        tabIndex={0}
                        aria-label={navCategory.title}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          variant: "text.sansBody03Regular",
                          gap: "s400",
                          ...NavLinkTextStyles,
                        }}
                        onClick={() => setIsDrawerOpen(false)}
                      >
                        {navCategory.title}
                      </NavLink>
                    ) : (
                      <MsButton
                        ref={categoryRefs.current[index] as RefObject<HTMLButtonElement>}
                        data-testid={`mobile-nav-${navCategory.slug}`}
                        variant="link"
                        key={index}
                        onClick={() => showAccordion(index)}
                        tabIndex={0}
                        aria-label={navCategory.title}
                        aria-expanded={false}
                        TrailingIcon={ChevronIcon}
                        sx={{
                          p: 0,
                          span: {
                            variant: "text.sansBody03Regular",
                            justifyContent: "space-between",
                            span: {
                              ...NavLinkTextStyles,
                            },
                            svg: {
                              height: "14px",
                              color: "grey400",
                            },
                          },
                        }}
                      >
                        {navCategory.title}
                      </MsButton>
                    ),
                  )}
                </Box>
                <Divider aria-hidden={true} sx={{ my: "s400", color: "grey100" }} />

                <Flex
                  sx={{
                    flexDirection: "column",
                    gap: "s400",
                    alignSelf: "stretch",
                    pb: "s1400",
                  }}
                >
                  {isAuthenticated ? (
                    <NavLink
                      variant="box"
                      to={accountAuthenticatedLink.to ?? ""}
                      aria-label={
                        accountAuthenticatedLink?.mobileLabel ?? accountAuthenticatedLink.label
                      }
                      onClick={() => setIsDrawerOpen(false)}
                    >
                      <Text variant="sansBody03Regular" sx={{ ...NavLinkTextStyles }}>
                        {accountAuthenticatedLink?.mobileLabel ?? accountAuthenticatedLink.label}
                      </Text>
                    </NavLink>
                  ) : (
                    <NavLink
                      variant="box"
                      to={loginLink.to ?? ""}
                      aria-label={loginLink?.mobileLabel}
                      onClick={() => setIsDrawerOpen(false)}
                    >
                      <Text variant="sansBody03Regular" sx={{ ...NavLinkTextStyles }}>
                        {loginLink?.mobileLabel ?? loginLink.label}
                      </Text>
                    </NavLink>
                  )}

                  {additionalLinks.map((link, index) => (
                    <NavLink
                      variant="box"
                      key={index}
                      to={link.to ?? ""}
                      aria-label={link.label}
                      onClick={() => setIsDrawerOpen(false)}
                    >
                      <Text variant="sansBody03Regular" sx={{ ...NavLinkTextStyles }}>
                        {link.mobileLabel ?? link.label}
                      </Text>
                    </NavLink>
                  ))}

                  {blogLink?.to && (
                    <NavLink
                      variant="box"
                      to={blogLink.to}
                      aria-label={blogLink.label}
                      onClick={() => setIsDrawerOpen(false)}
                    >
                      <Text variant="sansBody03Regular" sx={{ ...NavLinkTextStyles }}>
                        {blogLink.label}
                      </Text>
                    </NavLink>
                  )}
                </Flex>
              </motion.div>
              <MobileNavCategoryScreen
                navCategory={selectedNavCategory}
                closeAccordion={() => setSelectedNavCategory(null)}
              />
            </motion.menu>
          </RemoveScroll>
        </Box>
      )}
    </AnimatePresence>
  );
};
